import { useMsal } from '@azure/msal-react';
import clsx from 'clsx';
import { NavLink, useSearchParams } from 'react-router-dom';
import {
	Calendar,
	Check,
	ChevronDown,
	Clipboard,
	Home,
	Hospital,
	LogOut,
	PlusSquare,
	Receipt,
	Settings,
	Timeline,
	User,
	Users,
	type LucideIcon
} from 'lucide-react';
import { useIdentity } from '~/features/authentication/identity/IdentityContext';
import { Switch } from './shared/components/Switch';
import { useAppSettings } from './AppSettingsContext';
import { IconButtonPopover } from './shared/IconButtonPopover';
import { SelectField } from '~/shared/forms/controls/base-fields/SelectField';
import { UserTypeEnum, UserTypeOptions } from '@generated';
import { getEditUserRoute } from './pages/settings/routes';

interface MenuItem {
	icon: LucideIcon;
	label: string;
	path: string;
	type: 'new' | 'legacy' | 'both';
}

const menuItems: MenuItem[] = [
	{ icon: Home, label: 'Home', path: '/', type: 'both' },
	{ icon: PlusSquare, label: 'Intake', path: '/referrals', type: 'new' },
	{ icon: Users, label: 'Patients', path: '/patients', type: 'new' },
	{ icon: Calendar, label: 'Schedules', path: '/scheduling', type: 'new' },
	{ icon: Clipboard, label: 'Orders', path: '/orders', type: 'new' },
	{ icon: Check, label: 'QA', path: '/qa', type: 'new' },
	{ icon: Timeline, label: 'OASIS', path: '/oasis', type: 'new' },
	{ icon: Receipt, label: 'Billing', path: '/billing', type: 'new' },
	{ icon: Settings, label: 'Settings', path: '/settings', type: 'new' },
	{ icon: Calendar, label: 'Schedules', path: '/legacy-schedule', type: 'legacy' },
	{ icon: Clipboard, label: 'Referrals', path: '/legacy-referrals', type: 'legacy' },
	{ icon: Timeline, label: 'OASIS', path: '/oasis-tracking', type: 'legacy' },
	{ icon: Receipt, label: 'Wound Patients', path: '/legacy-wound-patients', type: 'legacy' },
	{ icon: Hospital, label: 'Hospitalizations', path: '/patienthospitalizations', type: 'legacy' },
	{ icon: Users, label: 'Users', path: '/settings/libraries/users', type: 'legacy' }
];

function useCheckLegacyLinkClick(showLegacyApp: boolean, setShowLegacyApp: (value: boolean) => void) {
	const [searchParams, setSearchParams] = useSearchParams();
	const referralNotificationToRespondTo = searchParams.get('referralNotificationIdResponse');

	if (!showLegacyApp && referralNotificationToRespondTo) {
		setShowLegacyApp(true);
	}
}
export function AppNavigation() {
	const { instance } = useMsal();
	const {
		userIsAnAdmin,
		loggedInUser,
		selectedUserType: { selectedUserTypeId, setSelectedUserTypeId }
	} = useIdentity();
	const { showLegacyApp, setShowLegacyApp } = useAppSettings();
	useCheckLegacyLinkClick(showLegacyApp, setShowLegacyApp);
	const isAnAdmin = userIsAnAdmin();

	const logOut = async () => {
		// Logout from MSAL and redirect to home page
		await instance.logoutRedirect({
			postLogoutRedirectUri: window.location.origin
		});
	};

	const multipleUserTypes = loggedInUser.additionalUserTypes.length > 0;
	const userTypeOptions = UserTypeOptions.filter(
		x => x.id === loggedInUser.primaryUserTypeId || loggedInUser.additionalUserTypes.some(y => y.id === x.id)
	).map(x => {
		if (x.id !== UserTypeEnum.Clinician || !loggedInUser.clinicalTypeLabel) {
			return x;
		}
		return {
			...x,
			label: loggedInUser.clinicalTypeLabel
		};
	});

	return (
		<div className="flex min-h-[40px] items-center gap-1 bg-primary-700 px-4 text-white">
			<div className="mr-3 select-none text-sm font-bold tracking-wide text-white">Duro</div>
			<div className="flex flex-1 items-center gap-0.5">
				{menuItems
					.filter(x => (showLegacyApp ? x.type !== 'new' : x.type !== 'legacy'))
					.map(item => (
						<MenuItem key={`${item.label}${item.path}`} {...item} />
					))}
			</div>

			<div className="flex items-center gap-3">
				{isAnAdmin && (
					<Switch isSelected={showLegacyApp} onChange={setShowLegacyApp} className="mr-2 text-white" aria-label="Legacy Toggle">
						Legacy
					</Switch>
				)}
				<div className="flex gap-2">
					{multipleUserTypes && (
						<IconButtonPopover
							Icon={ChevronDown}
							aria-label="Change User Type"
							appearance="ghost"
							iconClassName="size-4"
							className="rounded-none px-4 text-white active:bg-white/20 hover:bg-white/10 focus:border-transparent focus:ring-0"
							popoverClassName="w-64 p-3"
							popoverText={
								<SelectField
									label="Change User Type"
									items={userTypeOptions}
									value={selectedUserTypeId}
									onChange={value => setSelectedUserTypeId(value ? parseInt(value.toString()) : loggedInUser.primaryUserTypeId)}
									density="compact"
								/>
							}
						/>
					)}
					<div className="flex">
						{loggedInUser && <MenuItem icon={User} path={getEditUserRoute(loggedInUser.id)} />}

						<button
							type="button"
							onClick={logOut}
							className="flex items-center px-4 py-2 text-sm transition-colors hover:bg-white/10"
							aria-label="Log Out"
						>
							<LogOut className="size-4" />
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

function MenuItem(props: { icon: LucideIcon; label?: string; path: string }) {
	return (
		<NavLink
			className={({ isActive }) =>
				clsx(
					'flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors duration-100',
					isActive ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
				)
			}
			to={props.path}
		>
			{props.icon && <props.icon className={props.label ? 'size-3' : 'size-4'} />}
			{props.label}
		</NavLink>
	);
}
